import { useEffect, useRef } from 'react';
import { gsap, ScrollTrigger, motionToken } from '../../utils/gsap';

/**
 * Pinna la sezione a schermo intero per la durata dello scroll e lascia costruire
 * una timeline scrubbed al chiamante (build riceve gsap.timeline() già configurata).
 *   const ref = useScrollPin((tl) => { tl.to('.a', {...}).to('.b', {...}, '<'); });
 */
export function useScrollPin(build, { pinSpacing = true, end = '+=150%' } = {}) {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const ctx = gsap.context(() => {
      const tl = gsap.timeline({
        scrollTrigger: {
          trigger: el, start: 'top top', end, scrub: 1,
          pin: true, pinSpacing, anticipatePin: 1,
        },
      });
      build(tl, el);
    }, el);
    return () => ctx.revert();
  }, []);
  return ref;
}

/** Scroll-jacking orizzontale: pinna e trasla in orizzontale i figli diretti in base allo scroll verticale. */
export function useHorizontalScroll() {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const track = el.firstElementChild;
    const ctx = gsap.context(() => {
      const distance = () => track.scrollWidth - el.clientWidth;
      gsap.to(track, {
        x: () => -distance(),
        ease: 'none',
        scrollTrigger: {
          trigger: el, start: 'top top', end: () => '+=' + distance(),
          scrub: 1, pin: true, invalidateOnRefresh: true,
        },
      });
    }, el);
    return () => ctx.revert();
  }, []);
  return ref;
}

/** Parallax semplice: sposta l'elemento verticalmente in base alla profondità (token --parallax-depth-*). */
export function useParallaxLayer(depth = parseFloat(motionToken('--parallax-depth-near', '0.15'))) {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const ctx = gsap.context(() => {
      gsap.to(el, {
        yPercent: depth * 100,
        ease: 'none',
        scrollTrigger: { trigger: el, start: 'top bottom', end: 'bottom top', scrub: true },
      });
    }, el);
    return () => ctx.revert();
  }, [depth]);
  return ref;
}

// Offset "nascosto" per direzione — usati sia per l'ingresso (parte da qui,
// arriva a stato naturale) sia per l'uscita (parte da stato naturale, arriva
// qui). 'none' = nessuna animazione su quel lato (elemento sempre come da
// flusso naturale, mai nascosto). Stesso set di valori riusato per
// animIn/animOut ovunque: il significato è sempre "il lato/lo stile da cui
// l'elemento appare o verso cui scompare", mai invertito arbitrariamente.
const REVEAL_OFFSETS = {
  none: null,
  fade: { opacity: 0 },
  scale: { opacity: 0, scale: 0.85 },
  top: { opacity: 0, y: -40 },
  bottom: { opacity: 0, y: 40 },
  left: { opacity: 0, x: -40 },
  right: { opacity: 0, x: 40 },
};

function revealHiddenStyle(type) {
  const offset = REVEAL_OFFSETS[type];
  return offset ? { x: 0, y: 0, scale: 1, ...offset } : null;
}

/**
 * Rivelazione allo scroll, reversibile: l'elemento anima dallo stato
 * "nascosto" di `animIn` a quello naturale quando entra in viewport
 * (in avanti o tornando indietro), e dallo stato naturale allo stato
 * "nascosto" di `animOut` quando esce (in avanti o tornando indietro) —
 * a differenza di useScrollPin/useHorizontalScroll (pin+scrub, sempre
 * legati alla posizione di scroll) qui l'animazione è "play/reverse" sui
 * 4 eventi standard di ScrollTrigger (onEnter/onLeave/onEnterBack/
 * onLeaveBack), non scrubbed — l'elemento non deve necessariamente
 * restare a schermo intero per attivarsi.
 *   const ref = useScrollReveal({ animIn: 'bottom', animOut: 'top' });
 */
export function useScrollReveal({ animIn = 'fade', animOut = 'fade', start = 'top 85%', end = 'bottom 15%' } = {}) {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const inHidden = revealHiddenStyle(animIn);
    const outHidden = revealHiddenStyle(animOut);
    // Durata/ease propri, non il default globale (--duration-base, 0.2s —
    // pensato per micro-interazioni UI, hover/click). Un reveal allo scroll
    // deve durare abbastanza da essere notato mentre si scrolla con calma:
    // --duration-cinematic/--ease-cinematic del tema sono pensati apposta
    // per questo (stessi token usati dagli scroll-pin a tutto schermo).
    const duration = parseFloat(motionToken('--duration-cinematic', '900ms')) / 1000 || 0.9;
    const ease = motionToken('--ease-cinematic', 'power2.out');
    const shown = { opacity: 1, x: 0, y: 0, scale: 1, duration, ease };

    const ctx = gsap.context(() => {
      if (inHidden) gsap.set(el, inHidden);
      ScrollTrigger.create({
        trigger: el, start, end,
        onEnter: () => gsap.to(el, shown),
        onEnterBack: () => gsap.to(el, shown),
        onLeave: () => { if (outHidden) gsap.to(el, { ...outHidden, duration, ease }); },
        onLeaveBack: () => { if (inHidden) gsap.to(el, { ...inHidden, duration, ease }); },
      });
    }, el);
    return () => ctx.revert();
  }, [animIn, animOut, start, end]);
  return ref;
}

/** Hover magnetico su bottoni/link — usa --magnetic-strength e --magnetic-radius del tema. */
export function useMagneticHover() {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const strength = parseFloat(motionToken('--magnetic-strength', '0.35'));
    const radius   = parseFloat(motionToken('--magnetic-radius', '120px'));
    function onMove(e) {
      const rect = el.getBoundingClientRect();
      const dx = e.clientX - (rect.left + rect.width / 2);
      const dy = e.clientY - (rect.top + rect.height / 2);
      const dist = Math.hypot(dx, dy);
      if (dist > radius) return;
      gsap.to(el, { x: dx * strength, y: dy * strength, duration: 0.4, ease: 'power3.out' });
    }
    function onLeave() {
      gsap.to(el, { x: 0, y: 0, duration: 0.6, ease: motionToken('--ease-spring', 'elastic.out(1, 0.4)') });
    }
    el.addEventListener('mousemove', onMove);
    el.addEventListener('mouseleave', onLeave);
    return () => {
      el.removeEventListener('mousemove', onMove);
      el.removeEventListener('mouseleave', onLeave);
    };
  }, []);
  return ref;
}
